import { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ThreeBackgroundCube from './ThreeBackgroundCube';
import { setupCamera } from '../../Scene/setupThreeEditor';
import { load3DSceneTextures } from '../../utils/LOD_helper';

const BackgroundCube = ({
	scene,
	backgroundUrl,
	opacityMapUrl,
	camera,
	renderer,
	linkedScenes,
	imageIntegrity,
	useWebp,
	skipLargest,
	controller,
	transition,
	onBackgroundReady = () => {},
	onBackgroundLoaded = () => {},
	materialProperties = null,
	sceneId,
	useSceneBackgroundAsEnvMap,
}) => {
	const cube = useRef();
	const didCameraReset = useRef(false);

	const depthTest = useMemo(() => {
		return materialProperties.depth_test;
	}, [materialProperties.depth_test]);

	useEffect(() => {
		cube.current = new ThreeBackgroundCube(
			camera,
			controller,
			renderer,
			transition,
		);
		cube.current.addToScene(scene);

		return () => {
			cube.current.dispose();
			cube.current.removeFromScene();
		};
	}, []); // eslint-disable-line

	useEffect(() => {
		if (!didCameraReset.current) {
			setupCamera(camera);
			controller.update();
			didCameraReset.current = true;
		}
		if (useSceneBackgroundAsEnvMap) {
			load3DSceneTextures(backgroundUrl)
				.then((texture) => {
					scene.environment = texture;
				})
				.catch((err) => {
					console.error('=> Error loading scene images', err);
				});
		}
		return () => {
			cube.current.dispose();
			scene.environment = null;
		};
	}, [sceneId]);

	useEffect(() => {
		if (backgroundUrl) {
			cube.current.removeAlphaMaps();
			cube.current.loadCubeTextureFromPriorityArray(
				backgroundUrl,
				opacityMapUrl,
				imageIntegrity,
				useWebp,
				skipLargest,
				onBackgroundReady,
				onBackgroundLoaded,
				materialProperties,
			);
		}
	}, [backgroundUrl, opacityMapUrl]);

	useEffect(() => {
		cube.current.setDepthTest(depthTest);
	}, [depthTest]);

	useEffect(() => {
		cube.current.camera = camera;
	}, [camera]);

	return null;
};

BackgroundCube.propTypes = {
	backgroundUrl: PropTypes.string,
};

export default BackgroundCube;
